interface PlanBadgeProps {
  status:
    | 'free'
    | 'trial'
    | 'active'
    | 'past_due'
    | 'expired'
    | 'canceled';
  tier?: string | null;
  trialEndsAt?: string | null;
  graceDaysLeft?: number;
  daysUntilReadOnly?: number | null;
  onClick?: () => void;
}

export default function PlanBadge({
  status,
  tier,
  trialEndsAt,
  graceDaysLeft,
  daysUntilReadOnly,
  onClick,
}: PlanBadgeProps) {
  const getBadgeStyles = () => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-700 border-green-200';
      case 'trial':
        return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'past_due':
        return 'bg-yellow-100 text-yellow-700 border-yellow-200';
      case 'expired':
        return 'bg-red-100 text-red-700 border-red-200';
      case 'canceled':
        return 'bg-gray-100 text-gray-700 border-gray-200';
      case 'free':
      default:
        return 'bg-paper-200 text-ink-600 border-paper-300';
    }
  };

  const getLabel = () => {
    const tierLabel = tier
      ? tier.charAt(0).toUpperCase() + tier.slice(1)
      : null;
    switch (status) {
      case 'active':
        return tierLabel ? `${tierLabel}` : 'Active';
      case 'trial':
        return 'Trial';
      case 'past_due':
        return 'Past Due';
      case 'expired':
        return 'Expired';
      case 'canceled':
        return 'Canceled';
      case 'free':
      default:
        return 'Free';
    }
  };

  const getSubtitle = () => {
    if (status === 'trial' && trialEndsAt) {
      const days = Math.ceil(
        (new Date(trialEndsAt).getTime() - Date.now()) /
          (1000 * 60 * 60 * 24)
      );
      return days > 0 ? `${days} days left` : 'Ending soon';
    }

    if (
      status === 'past_due' &&
      graceDaysLeft &&
      graceDaysLeft > 0
    ) {
      return `${graceDaysLeft} days grace`;
    }

    if (
      status === 'expired' &&
      daysUntilReadOnly &&
      daysUntilReadOnly > 0
    ) {
      return `${daysUntilReadOnly} days until read-only`;
    }

    return null;
  };

  const subtitle = getSubtitle();

  return (
    <button
      onClick={onClick}
      className={`
        inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium
        border transition-colors hover:opacity-80
        ${getBadgeStyles()}
        ${onClick ? 'cursor-pointer' : 'cursor-default'}
      `}
    >
      <span>{getLabel()}</span>
      {subtitle && (
        <span className="opacity-75">• {subtitle}</span>
      )}
    </button>
  );
}
